import forEach from './_helpers/foreach.js';
import forOwn from './_helpers/forown.js';
import hasOwn from './_helpers/hasown.js';
import privateKey from './_helpers/privatekey.js';
// static methods and properties of classes will be hidden under Symbol('staticNames')
var staticNamesProperty = privateKey('staticNames');
// the own symbol keys; none where the browser has no Symbol (IE 11)
function ownSymbols(object) {
  return Object.getOwnPropertySymbols ? Object.getOwnPropertySymbols(object) : [];
}
// IE 11 has no Reflect, and no ES classes or methods that `new` refuses
function hasReflect() {
  return typeof Reflect === 'object';
}
function isConstructor(fn) {
  if (!hasReflect()) return true;
  try {
    Reflect.construct(Object, [], fn);
    return true;
  } catch (_unused) {
    return false;
  }
}
// an ES class or a built-in such as Array: only Reflect.construct builds it for a subclass
function hasReadOnlyPrototype(fn) {
  var _Object$getOwnPropert;
  return ((_Object$getOwnPropert = Object.getOwnPropertyDescriptor(fn, 'prototype')) == null ? void 0 : _Object$getOwnPropert.writable) === false;
}
// what `new` built `this` for, read the way Babel compiles new.target: a parent called with `apply` from a subclass
// constructor gets the subclass, so a chain over an ES class works in src/ and in the ES5 build alike
function newTarget(self, Constructor) {
  return self instanceof Constructor ? self.constructor : undefined;
}
// `constructor() {}` in an object literal is a method, which `new` refuses, and an ES class has a read-only
// prototype; both get a wrapper
function toConstructor(fn) {
  if (isConstructor(fn)) {
    if (hasReadOnlyPrototype(fn)) {
      var Constructor = function Constructor() {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        return Reflect.construct(fn, args, newTarget(this, Constructor));
      };
      return Constructor;
    }
    return fn;
  }
  return function Constructor() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    var result = fn.apply(this, args);
    return result !== null && (typeof result === 'object' || typeof result === 'function') ? result : this;
  };
}
// builds a class from a prototype object (with an optional constructor and "extends") and static members
var Class = function Class(prototype, staticProps) {
  // extends is kept for backward compatibility
  // its statics are read by name
  var Parent = prototype.extends;
  var Constructor;
  if (hasOwn(prototype, 'constructor')) {
    Constructor = toConstructor(prototype.constructor);
  } else if (Parent) {
    // like an ES class without a constructor, call the parent constructor with the same arguments
    Constructor = function EmptyConstructor() {
      var target = newTarget(this, Constructor);
      // a plain function parent runs on `this`, so `Child.call(this)` from a subclass constructor works too
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }
      return target && hasReadOnlyPrototype(Parent) && hasReflect() ? Reflect.construct(Parent, args, target) : Parent.apply(this, args);
    };
  } else {
    Constructor = function EmptyConstructor() {};
  }
  // inherit proto from class parent or empty object
  var proto = Object.create(Parent ? Parent.prototype : {});
  var parentStaticNames = Parent ? Parent[staticNamesProperty] : undefined;
  // descriptors, so getters and setters stay accessors; "extends" is an option, not a member
  var members = {};
  forEach(Object.getOwnPropertyNames(prototype).concat(ownSymbols(prototype)), function (key) {
    if (key !== 'constructor' && key !== 'extends') {
      members[key] = Object.getOwnPropertyDescriptor(prototype, key);
    }
  });
  Object.defineProperties(proto, members);
  // non-enumerable, as in ES classes
  Object.defineProperty(proto, 'constructor', {
    value: Constructor,
    writable: true,
    configurable: true,
    enumerable: false
  });
  // inherit staric properties of a parent
  if (typeof parentStaticNames === 'object') {
    var staticNames = Constructor[staticNamesProperty] || {};
    // hidden from for-in where the key is a string (IE 11)
    Object.defineProperty(Constructor, staticNamesProperty, {
      value: staticNames,
      writable: true,
      configurable: true
    });
    forOwn(parentStaticNames, function (_, name) {
      Constructor[name] = Parent[name];
      staticNames[name] = true;
    });
    // inherit static properties of a parent when their keys are symbols
    forEach(ownSymbols(parentStaticNames), function (symbol) {
      Constructor[symbol] = Parent[symbol];
      staticNames[symbol] = true;
    });
  }
  // extend Constructor with passed static properties
  if (typeof staticProps === 'object') {
    var _staticNames = Constructor[staticNamesProperty] || {};
    // hidden from for-in where the key is a string (IE 11)
    Object.defineProperty(Constructor, staticNamesProperty, {
      value: _staticNames,
      writable: true,
      configurable: true
    });
    forOwn(staticProps, function (value, key) {
      Constructor[key] = value;
      _staticNames[key] = true;
    });
    // extend Constructor with passed static properties if their keys are symbols
    forEach(ownSymbols(staticProps), function (symbol) {
      Constructor[symbol] = staticProps[symbol];
      _staticNames[symbol] = true;
    });
  }
  Constructor.prototype = proto;
  // if new Class({}) is called return its instance
  if (this instanceof Class) {
    return new Constructor();
  }
  return Constructor;
};
export default Class;